// Who is calling — the first thing an agent does, and the thing that decides how
// long the call takes (SPEC.md §6).
//
// The panel is one flow with three resolved slots (organization → location →
// requester) and two ways into it:
//
//   * Account number. Unique, so a hit resolves the organization *and* the store
//     in one step (docs/API.md §4.1 `matched_location`) and drops the agent
//     straight on the requester list. Typing a complete account number resolves it
//     without a keystroke more — this is the fast path and the one agents use most.
//   * Organization by name, then the person within it. The location step is
//     skipped whenever the organization has one location, because there is no
//     decision to make.
//
// The "Person" mode is the bypass for one-off callers with no organization: SPEC
// §6 wants that to be a fast path, not a gate you have to argue your way past.
//
// Nothing here is a modal. Esc closes a list or cancels the inline create and
// leaves the agent where they were; tab moves on. A focus trap on this screen is a
// dropped call.
//
// THEMING. Everything here is a semantic token (bg-surface / bg-sunken / text-ink*
// / border-line*) except three things that genuinely cannot be one:
//
//   * Link colour. brand-700 is 6.6:1 on white but 2.6:1 on the dark page, so every
//     link on this screen is `text-brand-700 dark:text-brand-300` (9.0:1).
//   * The keyboard cursor in the store list, which matches ResultList's: a solid
//     brand-600 fill, identical in both themes. See the note in ResultList.jsx.
//   * The duplicate-contact notice, which is deliberately blue rather than amber —
//     see the comment where it is rendered.

import { useCallback, useEffect, useRef, useState } from 'react';

import { api } from '../../api/client.js';
import useTypeahead from './useTypeahead.js';
import useListNavigation from './useListNavigation.js';
import ResultList from './ResultList.jsx';
import RequesterList from './RequesterList.jsx';
import NewCustomerInline from './NewCustomerInline.jsx';

import { filterStores, storeCountLabel, MAX_VISIBLE_STORES } from './store-search.js';

export const EMPTY_CALLER = { organization: null, location: null, customer: null };

/** Keep only what the form needs; the search row carries more than we want in a draft. */
function organizationShape(row) {
  return {
    id: row.id,
    name: row.name,
    domains: row.domains ?? [],
    location_count: row.location_count ?? null,
  };
}

export default function CallerPanel({ value, onChange, onRequesterChosen, searchRef, people }) {
  const { organization, location, customer } = value;

  const [mode, setMode] = useState('org');
  const [query, setQuery] = useState('');
  const [locationState, setLocationState] = useState({ orgId: null, rows: null, status: 'idle' });
  const [locationSkipped, setLocationSkipped] = useState(false);
  const [creating, setCreating] = useState(false);
  const [notice, setNotice] = useState(null);

  // Effects below need the current selection without listing every part of it as a
  // dependency — re-running a fetch because an unrelated slot changed is how a
  // lookup ends up firing twice per keystroke.
  const valueRef = useRef(value);
  valueRef.current = value;

  const requesterFilterRef = useRef(null);
  const locationListRef = useRef(null);

  const searchOrganizations = useCallback(
    (q, signal) =>
      api
        .get(`/organizations?q=${encodeURIComponent(q)}&limit=10`, { signal })
        .then((response) => response.data ?? []),
    [],
  );

  const searchPeople = useCallback(
    (q, signal) =>
      api
        .get(`/customers?q=${encodeURIComponent(q)}&limit=10`, { signal })
        .then((response) => response.data ?? []),
    [],
  );

  const typeahead = useTypeahead({
    query,
    fetcher: mode === 'org' ? searchOrganizations : searchPeople,
    enabled: mode === 'org' ? !organization : !customer,
  });

  const selectOrganization = useCallback(
    (row) => {
      setLocationSkipped(false);
      setCreating(false);
      setNotice(null);
      onChange({
        organization: organizationShape(row),
        location: row.matched_location ?? null,
        customer: null,
      });
    },
    [onChange],
  );

  const selectPerson = useCallback(
    (person) => {
      setCreating(false);
      setNotice(null);
      onChange({
        organization: person.organization ? organizationShape(person.organization) : null,
        location: person.location ?? null,
        customer: person,
      });
      onRequesterChosen?.();
    },
    [onChange, onRequesterChosen],
  );

  const selectRequester = useCallback(
    (person) => {
      const current = valueRef.current;
      // A requester carries their own store, so a multi-location organization
      // still ends up with a location without the agent picking one.
      const inferred = current.location
        ?? (locationState.rows ?? []).find((row) => row.id === person.location_id)
        ?? null;
      setCreating(false);
      setNotice(null);
      onChange({ ...current, location: inferred, customer: person });
      onRequesterChosen?.();
    },
    [onChange, onRequesterChosen, locationState.rows],
  );

  const listNav = useListNavigation(typeahead.items, {
    onSelect: (item) => (mode === 'org' ? selectOrganization(item) : selectPerson(item)),
    onEscape: () => setQuery(''),
  });

  // A complete account number is unambiguous, so resolving it needs no confirming
  // click: the API only returns `matched_location` when the hit came via an
  // account number, and account numbers are unique (docs/API.md §4.1).
  useEffect(() => {
    if (mode !== 'org' || organization || typeahead.status !== 'ready') return;
    const typed = typeahead.query.trim().toLowerCase();
    if (!typed) return;
    const exact = typeahead.items.find(
      (row) => row.matched_location?.account_number?.toLowerCase() === typed,
    );
    if (exact) selectOrganization(exact);
  }, [mode, organization, typeahead.status, typeahead.items, typeahead.query, selectOrganization]);

  useEffect(() => {
    if (!organization || location) return undefined;
    if (locationState.orgId === organization.id) return undefined;

    const controller = new AbortController();
    let cancelled = false;
    setLocationState({ orgId: organization.id, rows: null, status: 'loading' });

    api
      .get(`/organizations/${organization.id}/locations`, { signal: controller.signal })
      .then((response) => {
        if (cancelled) return;
        const rows = response.data ?? [];
        setLocationState({ orgId: organization.id, rows, status: 'ready' });
        // Skip the step automatically when there is only one store (SPEC.md §6).
        const current = valueRef.current;
        if (rows.length === 1 && current.organization?.id === organization.id && !current.location) {
          onChange({ ...current, location: rows[0] });
        }
      })
      .catch((error) => {
        if (cancelled || error?.name === 'AbortError') return;
        setLocationState({ orgId: organization.id, rows: [], status: 'error' });
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
    // `locationState.orgId` is deliberately NOT a dependency: this effect WRITES it, in
    // the `status: 'loading'` set above. Listing it made the effect re-run the instant it
    // set it, and the re-run's cleanup aborted the fetch the same effect had just
    // started — the panel sat on "Loading stores…" forever with no request in flight to
    // clear it. The guard above still reads the render-time value, which is exactly the
    // question it asks: "have I already fetched for this organization?"
    //
    // `onChange` must stay referentially stable — New.jsx passes `setCaller`, a useState
    // setter. An inline arrow here would turn this into a refetch loop.
  }, [organization, location, onChange]);

  const locationRows = locationState.orgId === organization?.id ? locationState.rows : null;
  const locationPending = Boolean(organization) && !location && !locationSkipped && locationRows === null;
  const needsLocation = Boolean(organization) && !location && !locationSkipped && (locationRows?.length ?? 0) > 0;
  // Hold the requester step back until the store question is settled, rather than
  // rendering it and swapping it out a moment later — a list that appears under
  // the caret and then moves is how the wrong person gets picked.
  const showRequester = Boolean(organization) && !customer && !locationPending && !needsLocation;

  // Move the caret to whatever the agent has to answer next. This is the whole
  // point of the panel: pick an organization and you are already typing the filter
  // over its people.
  useEffect(() => {
    if (!organization || customer || creating || locationPending) return;
    if (needsLocation) locationListRef.current?.focus();
    else requesterFilterRef.current?.focus();
  }, [organization, customer, creating, needsLocation, locationPending]);

  useEffect(() => {
    function onKeyDown(event) {
      if (!event.altKey || event.ctrlKey || event.metaKey) return;
      if (event.key !== 'n' && event.key !== 'N') return;
      if (valueRef.current.customer) return;
      event.preventDefault();
      setCreating(true);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  function switchMode(next) {
    setMode(next);
    setQuery('');
    setCreating(false);
    setNotice(null);
    searchRef?.current?.focus();
  }

  function resetOrganization() {
    setQuery('');
    setLocationSkipped(false);
    setNotice(null);
    setMode('org');
    onChange(EMPTY_CALLER);
    // The search field is the one that just disappeared; give it the caret back
    // once React has put it on screen again.
    window.requestAnimationFrame(() => searchRef?.current?.focus());
  }

  function handleCreated(person, { created } = {}) {
    const current = valueRef.current;
    onChange({
      organization: current.organization ?? (person.organization ? organizationShape(person.organization) : null),
      location: current.location ?? person.location ?? null,
      customer: person,
    });
    setCreating(false);
    // The duplicate case has to say so out loud: an agent who thinks they just
    // created this person also thinks the phone number they typed was saved.
    setNotice(
      created
        ? null
        : `${person.name ?? person.email} was already on file. Selected them instead of creating a duplicate.`,
    );
    onRequesterChosen?.();
  }

  const showSearch = mode === 'org' ? !organization && !customer : !customer;

  return (
    <section className="rounded-lg border border-line bg-surface p-4">
      <h2 className="text-sm font-semibold text-ink">Caller</h2>

      {showSearch && (
        <>
          <div role="group" aria-label="Lookup mode" className="mt-2 flex gap-1">
            {[
              ['org', 'Account / organization'],
              ['person', 'Person'],
            ].map(([key, label]) => (
              <button
                key={key}
                type="button"
                aria-pressed={mode === key}
                onClick={() => switchMode(key)}
                // The unpressed chip already sits on bg-sunken, so its hover has to
                // be one step further out than the usual surface→sunken hover;
                // bg-line is the next stop on the neutral ramp in both themes
                // (lighter in dark, darker in light) and is the only token between
                // sunken and text.
                className={`rounded-md px-2 py-1 text-xs font-medium transition-colors ${
                  mode === key
                    ? 'bg-brand-600 text-white'
                    : 'bg-sunken text-ink-secondary hover:bg-line hover:text-ink'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="mt-2">
            <label className="field-label" htmlFor="caller-search">
              {mode === 'org' ? 'Account number or organization' : 'Name, email or phone'}
            </label>
            <input
              id="caller-search"
              ref={searchRef}
              className="field-input"
              role="combobox"
              aria-expanded={typeahead.items.length > 0}
              aria-controls="caller-listbox"
              aria-autocomplete="list"
              aria-activedescendant={
                typeahead.items.length ? `caller-option-${listNav.activeIndex}` : undefined
              }
              aria-describedby="caller-search-hint"
              autoComplete="off"
              autoFocus
              placeholder={mode === 'org' ? 'e.g. BB-0142 or Bluebird' : 'e.g. Maria, 5550142'}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={listNav.handleKeyDown}
            />
            <p id="caller-search-hint" className="mt-1 text-xs text-ink-muted">
              {mode === 'org'
                ? 'A full account number resolves the store on its own. ↑↓ to choose, Enter to pick.'
                : 'Searches every contact, including callers with no organization.'}
            </p>
          </div>

          {typeahead.status === 'error' && (
            <p role="alert" className="mt-2 text-sm text-red-700 dark:text-red-300">
              {typeahead.error?.message ?? 'The lookup failed.'}
            </p>
          )}

          {typeahead.items.length > 0 && (
            <ResultList
              idPrefix="caller"
              label={mode === 'org' ? 'Organizations' : 'People'}
              items={typeahead.items}
              activeIndex={listNav.activeIndex}
              onHover={listNav.setActiveIndex}
              onSelect={(item) => (mode === 'org' ? selectOrganization(item) : selectPerson(item))}
              getKey={(item) => item.id}
              renderItem={(item) =>
                mode === 'org' ? (
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="font-medium">{item.name}</span>
                    {/* text-ink-muted would be 1.2:1 on the highlighted row's
                        brand-600 fill; brand-50 on brand-600 is 4.7:1. */}
                    <span className="truncate text-xs text-ink-muted group-data-[active=true]:text-brand-50">
                      {item.matched_location
                        ? `${item.matched_location.account_number} · ${item.matched_location.name}`
                        : `${item.location_count ?? 0} location${item.location_count === 1 ? '' : 's'}`}
                    </span>
                  </div>
                ) : (
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="font-medium">{item.name ?? item.email}</span>
                    <span className="truncate text-xs text-ink-muted group-data-[active=true]:text-brand-50">
                      {[item.organization?.name ?? item.company, item.email].filter(Boolean).join(' · ')}
                    </span>
                  </div>
                )
              }
            />
          )}

          {typeahead.isActive && typeahead.status === 'ready' && typeahead.items.length === 0 && (
            <p className="mt-2 text-sm text-ink-muted">
              No matches for “{typeahead.query}”.
            </p>
          )}

          {mode === 'person' && !creating && (
            <button
              type="button"
              className="btn-ghost mt-2 px-0 text-brand-700 dark:text-brand-300"
              onClick={() => setCreating(true)}
            >
              + New contact with no organization <span className="text-xs text-ink-muted">(Alt+N)</span>
            </button>
          )}
        </>
      )}

      {organization && (
        <SelectedRow
          label="Organization"
          value={organization.name}
          hint={organization.domains?.length ? organization.domains.join(', ') : null}
          onChange={resetOrganization}
        />
      )}

      {organization && location && (
        <SelectedRow
          label="Store"
          value={location.name}
          hint={location.account_number}
          onChange={() => {
            setLocationSkipped(false);
            onChange({ ...value, location: null });
          }}
        />
      )}

      {organization && !location && locationSkipped && (
        <SelectedRow
          label="Store"
          value="Not identified"
          onChange={() => setLocationSkipped(false)}
        />
      )}

      {needsLocation && (
        <LocationStep
          rows={locationRows}
          firstButtonRef={locationListRef}
          onSelect={(row) => onChange({ ...value, location: row })}
          onSkip={() => setLocationSkipped(true)}
        />
      )}

      {locationPending && (
        <p className="mt-3 text-sm text-ink-muted">Loading stores…</p>
      )}

      {showRequester && (
        <RequesterList
          people={people?.orgId === organization.id ? people.rows : null}
          status={people?.orgId === organization.id ? people.status : 'loading'}
          error={people?.error}
          filterRef={requesterFilterRef}
          selectedLocationId={location?.id ?? null}
          onSelect={selectRequester}
          onCreateNew={() => setCreating(true)}
        />
      )}

      {customer && (
        <SelectedRow
          label="Requester"
          value={customer.name ?? customer.email}
          hint={[customer.email, customer.phone].filter(Boolean).join(' · ')}
          onChange={() => {
            setNotice(null);
            onChange({ ...value, customer: null });
          }}
        />
      )}

      {notice && (
        // Informational, not a warning — and deliberately NOT amber. Amber is spent
        // on internal notes (SPEC.md §7.4) and on the two page-level warning bands;
        // a third warm surface on the same screen as the composer's note treatment
        // is exactly the convergence that makes "warm = internal" stop meaning
        // anything. This takes the same blue pair badge-new uses, which already has
        // a checked dark counterpart (blue-950/blue-200, 11.6:1).
        <p
          role="status"
          className="mt-2 rounded-md px-2 py-1.5 text-xs ring-1 ring-inset
                     bg-blue-50 text-blue-900 ring-blue-600/20
                     dark:bg-blue-950 dark:text-blue-200 dark:ring-blue-400/30"
        >
          {notice}
        </p>
      )}

      {creating && (
        <NewCustomerInline
          organization={organization}
          location={location}
          onCreated={handleCreated}
          onCancel={() => {
            setCreating(false);
            (requesterFilterRef.current ?? searchRef?.current)?.focus();
          }}
        />
      )}
    </section>
  );
}

function SelectedRow({ label, value, hint, onChange }) {
  return (
    <div className="mt-3 flex items-start justify-between gap-2 rounded-md bg-sunken px-3 py-2">
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-wide text-ink-muted">{label}</div>
        <div className="truncate text-sm font-medium text-ink">{value}</div>
        {hint && <div className="truncate text-xs text-ink-muted">{hint}</div>}
      </div>
      <button
        type="button"
        className="shrink-0 text-xs font-medium text-brand-700 hover:underline dark:text-brand-300"
        onClick={onChange}
      >
        Change
      </button>
    </div>
  );
}

function LocationStep({ rows, firstButtonRef, onSelect, onSkip }) {
  const [query, setQuery] = useState('');
  // Filtered in the browser because the whole list is already here: §4.2 is unpaginated,
  // so there is no request per keystroke and no stale-response race. See store-search.js
  // for why this had to exist the day the cutover import landed 2,058 stores.
  const matches = filterStores(rows, query);
  const visible = matches.slice(0, MAX_VISIBLE_STORES);
  const countLabel = storeCountLabel(matches.length, visible.length, query);

  return (
    <div className="mt-3">
      <div className="field-label">Store</div>
      {/* The search box takes the focus the first row used to, because an agent with a
          caller on the line types the store number rather than scrolling to it. */}
      <input
        ref={firstButtonRef}
        type="search"
        className="field-input mb-1"
        placeholder="Store number, street, town or account number"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        aria-label="Search stores"
      />
      {/* The store list is focus-driven rather than activedescendant-driven, so
          `:focus` is the cursor here and it gets the same solid brand-600 fill the
          typeahead rows use — a brand-50 focus wash is 1.08:1 on white and invisible
          on a dark panel. */}
      <ul className="max-h-48 overflow-y-auto rounded-md border border-line bg-surface dark:border-line-strong dark:bg-sunken">
        {visible.map((row) => (
          <li key={row.id}>
            <button
              type="button"
              onClick={() => onSelect(row)}
              className="group flex w-full items-baseline justify-between gap-3 px-3 py-2 text-left text-sm text-ink
                         hover:bg-sunken dark:hover:bg-line
                         focus:bg-brand-600 focus:text-white focus:outline-none"
            >
              <span className="font-medium">{row.name}</span>
              <span className="text-xs text-ink-muted group-focus:text-brand-50">
                {row.account_number ?? '-'}
              </span>
            </button>
          </li>
        ))}
      </ul>
      {countLabel && <p className="mt-1 text-xs text-ink-muted">{countLabel}</p>}
      {/* The caller may not know which store they are, and the ticket does not
          need one — location is nullable on create (docs/API.md §3.3). */}
      <button type="button" className="btn-ghost mt-1 px-0" onClick={onSkip}>
        Not sure, skip
      </button>
    </div>
  );
}
